import { useMemo, useState } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { getIcon, iconCategories } from '@/lib/icons';

// Ignore accents, casse et séparateurs : « file text » retrouve « file-text »,
// et « donnees » retrouve la catégorie « Données & analyse »
function normalize(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]/g, '');
}

interface IconPickerProps {
  value: string;
  onChange: (icon: string) => void;
}

export function IconPicker({ value, onChange }: IconPickerProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const Preview = getIcon(value);

  const groups = useMemo(() => {
    const search = normalize(query);
    if (!search) return iconCategories;

    return iconCategories
      .map((category) => ({
        label: category.label,
        // Chercher un nom de catégorie retient tout son groupe
        icons: normalize(category.label).includes(search)
          ? category.icons
          : category.icons.filter((name) => normalize(name).includes(search)),
      }))
      .filter((category) => category.icons.length > 0);
  }, [query]);

  const resultCount = groups.reduce((total, group) => total + group.icons.length, 0);

  const select = (icon: string) => {
    onChange(icon);
    setOpen(false);
    setQuery('');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex h-10 w-full items-center gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        >
          <Preview className="w-4 h-4 shrink-0" />
          <span className="truncate">{value}</span>
          <ChevronDown className="w-4 h-4 ml-auto shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>

      <PopoverContent className="w-[340px] p-0" align="start">
        <div className="flex items-center gap-2 border-b px-3">
          <Search className="w-4 h-4 shrink-0 opacity-50" />
          <Input
            autoFocus
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Rechercher un pictogramme…"
            className="h-11 border-0 px-0 shadow-none focus-visible:ring-0"
          />
        </div>

        <div className="max-h-[300px] overflow-y-auto p-2">
          {resultCount === 0 && (
            <p className="py-8 text-center text-sm text-muted-foreground">
              Aucun pictogramme pour « {query} »
            </p>
          )}

          {groups.map((group) => (
            <div key={group.label} className="mb-3 last:mb-0">
              <p className="px-1 pb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                {group.label}
              </p>
              <div className="grid grid-cols-8 gap-1">
                {group.icons.map((name) => {
                  const Icon = getIcon(name);
                  return (
                    <button
                      key={`${group.label}-${name}`}
                      type="button"
                      title={name}
                      onClick={() => select(name)}
                      className={`flex h-9 items-center justify-center rounded-md transition-colors hover:bg-accent ${
                        name === value ? 'bg-accent ring-1 ring-ring' : ''
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="border-t px-3 py-2 text-[11px] text-muted-foreground">
          {query ? `${resultCount} pictogramme${resultCount > 1 ? 's' : ''}` : `${resultCount} pictogrammes disponibles`}
        </div>
      </PopoverContent>
    </Popover>
  );
}
